import { useMemo } from 'react'
import styled from 'styled-components'

import { StructureExportDto } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import entityColors from '@/styles/entityColors'

import { EntityTypeIcon } from '../../EntityTypeIcon'
import { useStructureExportTypeLabel } from './StructureImportPreview/hooks/useStructureExportTypeLabel'
import {
	countTypes,
	getStructureNodeKey,
	NON_VISIBLE_TYPES,
	TREE_INDENT_PX,
} from './StructureImportPreview/structureImportPreviewUtils'

type StructureImportPreviewProps = {
	structure: StructureExportDto
}

function StructureNode({
	node,
	depth = 0,
	getTypeLabel,
	emptyLabel,
}: {
	depth?: number
	emptyLabel: string
	getTypeLabel: (type?: StructureExportDto.TypeEnum) => string
	node: StructureExportDto
}) {
	const type = node.type ?? StructureExportDto.TypeEnum.ROOT
	const label = node.name ?? node.code ?? ''
	const visibleChildren =
		node.children?.filter(
			(child) => child.type !== StructureExportDto.TypeEnum.TABLE_COLUMN,
		) ?? []
	const hasChildren = visibleChildren.length > 0

	if (type === StructureExportDto.TypeEnum.ROOT) {
		return (
			<>
				{visibleChildren.map((child, index) => (
					<StructureNode
						key={getStructureNodeKey(child, depth, index)}
						node={child}
						depth={depth}
						getTypeLabel={getTypeLabel}
						emptyLabel={emptyLabel}
					/>
				))}
			</>
		)
	}

	return (
		<NodeBlock>
			<NodeRow $depth={depth}>
				<NodeIcon>
					<EntityTypeIcon type={type} />
				</NodeIcon>
				<NodeName>
					{label || <NodeUnnamed>{emptyLabel}</NodeUnnamed>}
				</NodeName>
				{node.code && node.code !== node.name && (
					<NodeCode>{node.code}</NodeCode>
				)}
				<NodeTypeBadge>{getTypeLabel(type)}</NodeTypeBadge>
			</NodeRow>
			{hasChildren &&
				visibleChildren.map((child, index) => (
					<StructureNode
						key={getStructureNodeKey(child, depth + 1, index)}
						node={child}
						depth={depth + 1}
						getTypeLabel={getTypeLabel}
						emptyLabel={emptyLabel}
					/>
				))}
		</NodeBlock>
	)
}

export const StructureImportPreview = ({
	structure,
}: StructureImportPreviewProps) => {
	const { t } = useAppContext()
	const getTypeLabel = useStructureExportTypeLabel()
	const emptyLabel = t('NOT_AVAILABLE_SHORT')

	const counts = useMemo(() => countTypes(structure, {}), [structure])

	const totalObjects = useMemo(
		() =>
			Object.entries(counts)
				.filter(([type]) => !NON_VISIBLE_TYPES.has(type))
				.reduce((sum, [, count]) => sum + count, 0),
		[counts],
	)

	const summaryChips = useMemo(
		() =>
			Object.entries(counts)
				.filter(([type]) => type !== 'ROOT')
				.sort(([a], [b]) => a.localeCompare(b))
				.map(([type, count]) => ({
					type: type as StructureExportDto.TypeEnum,
					count,
				})),
		[counts],
	)

	return (
		<Wrapper data-testid="structure-import-preview">
			<Header>
				<TreeTitle>{t('PREVIEW')}</TreeTitle>
				<TotalCount data-testid="structure-import-preview-total">
					{totalObjects} {totalObjects === 1 ? t('OBJECT') : t('OBJECTS')}
				</TotalCount>
			</Header>
			{summaryChips.length > 0 && (
				<SummaryChips data-testid="structure-import-preview-summary">
					{summaryChips.map(({ type, count }) => (
						<SummaryChip
							key={type}
							$color={entityColors[type]}
							data-testid={`structure-import-preview-chip-${type}`}
						>
							<EntityTypeIcon type={type} />
							<ChipLabel>{getTypeLabel(type)}</ChipLabel>
							<ChipCount>{count}</ChipCount>
						</SummaryChip>
					))}
				</SummaryChips>
			)}
			<TreeScroll data-testid="structure-import-preview-tree">
				<StructureNode
					node={structure}
					getTypeLabel={getTypeLabel}
					emptyLabel={emptyLabel}
				/>
			</TreeScroll>
		</Wrapper>
	)
}

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	margin-top: 12px;
	gap: 10px;
`

const Header = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
`

const TreeTitle = styled.div`
	font-weight: 600;
	font-size: 14px;
`

const TotalCount = styled.div`
	font-size: 12px;
	color: ${(props) => props.theme.colors?.text?.base ?? '#666'};
	opacity: 0.75;
`

const SummaryChips = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
`

const SummaryChip = styled.div<{ $color?: string }>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 4px 10px;
	font-size: 12px;
	background: #fafafa;
	border: 1px solid
		${(props) => props.theme.colors?.layout?.borderColor ?? '#ddd'};
	border-left: 3px solid ${(props) => props.$color ?? '#999'};
	border-radius: 12px;
`

const ChipLabel = styled.span`
	color: ${(props) => props.theme.colors?.text?.base ?? '#333'};
`

const ChipCount = styled.span`
	font-weight: 600;
	color: ${(props) => props.theme.colors?.text?.base ?? '#333'};
	background: #fff;
	padding: 0 6px;
	border-radius: 8px;
	border: 1px solid
		${(props) => props.theme.colors?.layout?.borderColor ?? '#ddd'};
`

const TreeScroll = styled.div`
	flex: 1;
	min-height: 0;
	overflow: auto;
	border: 1px solid
		${(props) => props.theme.colors?.layout?.borderColor ?? '#ddd'};
	border-radius: 6px;
	padding: 6px 4px;
	background: #fff;
`

const NodeBlock = styled.div`
	display: flex;
	flex-direction: column;
`

const NodeRow = styled.div<{ $depth: number }>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 8px 4px ${(props) => 8 + props.$depth * TREE_INDENT_PX}px;
	font-size: 13px;
	border-radius: 4px;
	position: relative;

	&:hover {
		background: ${(props) =>
			props.theme.colors?.layout?.treePanel?.headerBackgroundColor ??
			'#f3f3f3'};
	}

	${(props) =>
		props.$depth > 0 &&
		`
		&::before {
			content: '';
			position: absolute;
			left: ${props.$depth * TREE_INDENT_PX - 8}px;
			top: 50%;
			width: 8px;
			height: 1px;
			background: ${props.theme.colors?.layout?.borderColor ?? '#ddd'};
		}
	`}
`

const NodeIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	flex-shrink: 0;
`

const NodeName = styled.span`
	font-weight: 500;
	color: ${(props) => props.theme.colors?.text?.base ?? '#333'};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const NodeUnnamed = styled.span`
	color: ${(props) => props.theme.colors?.text?.base ?? '#999'};
	opacity: 0.6;
	font-style: italic;
`

const NodeCode = styled.span`
	font-size: 11px;
	color: ${(props) => props.theme.colors?.text?.base ?? '#666'};
	opacity: 0.7;
	font-family: monospace;
	background: #f5f5f5;
	padding: 1px 6px;
	border-radius: 3px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`

const NodeTypeBadge = styled.span`
	margin-left: auto;
	font-size: 10px;
	color: ${(props) => props.theme.colors?.text?.base ?? '#666'};
	opacity: 0.65;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	flex-shrink: 0;
`
